import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import PropTypes from "prop-types";
import { validate as uuidValidate } from "uuid";

import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";
import Icon from "@cayuse/shared-react.components.icon";
import IconButton from "@cayuse/shared-react.components.icon-button";
import Button from "@cayuse/shared-react.components.button";

import ExtraSettingsTabs from "containers/Form/SimplifiedBuilder/Components/ExtraSettings";
import {
	DefaultQuestionTypeSelector,
} from "containers/Form/SimplifiedBuilder/Components/Question/Footer/DefaultQuestionTypeSelector";
import { getSingleColumnComponents } from "containers/Form/SimplifiedBuilder/Components/Question/Footer/utils";
import questions from "containers/Form/SimplifiedBuilder/Questions";
import { useDictionary } from "hooks";
import { decodeFlowableId, mapType } from "utils/forms";
import { formSelectors } from "store/selectors";

import { QuestionLabel } from "./QuestionLabel";

import styles from "../styles.css";

export const QuestionFooter = ({
	CustomSettingsComponent,
	QuestionTypeSelector,
	completeFormModel,
	config,
	onChangeConfig,
	onDelete,
	hideRequired,
	hideComponentSelector,
	hideLabel,
}) => {
	const dictionary = useDictionary("simplifiedBuilder.components.Question.Footer");
	const [ showMoreSettings, setShowMoreSettings ] = useState(false);
	const { lastPublishedFormElements } = useSelector(formSelectors.content);

	const isDisabled = useMemo(
		() => config.id && lastPublishedFormElements && lastPublishedFormElements.includes(config.id),
		[ config, lastPublishedFormElements ],
	);
	const localConfig = useMemo(() => ({ ...config, type: config.cayuseType || config.type }), [ config ]);
	const localId = useMemo(
		() => decodeFlowableId(localConfig.newId || localConfig.id),
		[ localConfig ],
	);

	const [ shownId, setShownId ] = useState(uuidValidate(localId) ? "" : localId);

	const isFullColumn = useMemo(() => config.size === 12, [ config.size ]);

	const singleColumnComponents = useMemo(
		() => getSingleColumnComponents(),
		[],
	);

	const isSingleColumnType = useMemo(
		() => singleColumnComponents[mapType(config.type)],
		[ config.type, singleColumnComponents ],
	);

	const customOptions = useMemo(
		() => questions[localConfig.type]?.customOptions || [],
		[ localConfig.type ],
	);

	const onChangeId = value => {
		const newId = `${value || ""}`.replace(/[^\w-]/gi, "");
		setShownId(newId);
		onChangeConfig({ newId });
	};
	const onChangeCustomOption = (option, value) => onChangeConfig({
		extraSettings: {
			[option.type]: value,
		},
	});
	const onChangeRequired = isRequired => onChangeConfig({ isRequired });
	const onShowMoreSettingsToggle = () => setShowMoreSettings(!showMoreSettings);

	return (
		<>
			<hr className={styles.dividingLine} />
			<div className={styles.toolbar}>
				{!hideComponentSelector && (
					<label className="ui input" style={{ flexGrow: 3 }}>
						<span>{dictionary.questionType}</span>
						{isFullColumn && isSingleColumnType ? (
							<input type="text" value={isSingleColumnType.name} disabled />
						) : (
							<QuestionTypeSelector
								config={localConfig}
								onChangeConfig={onChangeConfig}
							/>
						)}
					</label>
				)}
				<div style={isFullColumn ? {} : { flexGrow: 1 }}>
					<QuestionLabel
						localConfig={localConfig}
						onChangeConfig={onChangeConfig}
						hideLabel={hideLabel}
						isFullColumn={isFullColumn}
						isSingleColumnType={isSingleColumnType}
					/>
					<label className="ui input" style={{ flexGrow: 1 }}>
						<span>{dictionary.key}</span>
						<input
							aria-label={`Question ${dictionary.key}`}
							type="text"
							disabled={isDisabled}
							value={shownId || ""}
							maxLength={30}
							onChange={event => onChangeId(event.target.value)}
							data-testid="question-footer-key"
						/>
					</label>
				</div>
				{customOptions.map(option => (
					<label className="ui input" key={option.label}>
						<span>{option.label}</span>
						<Checkbox
							label=""
							checked={localConfig.extraSettings[option.type]}
							onChange={(event, data) => onChangeCustomOption(option, data.checked)}
						/>
					</label>
				))}
				{!hideRequired && (
					<label className="ui input">
						<span>{dictionary.required}</span>
						<Checkbox
							label=""
							checked={localConfig.isRequired}
							onChange={(event, data) => onChangeRequired(data.checked)}
							data-testid="question-footer-required"
						/>
					</label>
				)}
				<div className={`ui input ${styles.center}`}>
					<IconButton
						aria-label="Delete question"
						onClick={onDelete}
						className={`${styles.noTextDecoration} ${styles.link}`}
						tertiary
					>
						<Icon size="bigger" name="trash" />
					</IconButton>
				</div>
				<div className={`ui input ${isFullColumn ? styles.center : styles.bottom}`}>
					<Button
						onClick={onShowMoreSettingsToggle}
						className={styles.link}
						secondary
						aria-controls={`extra-settings-${localId}`}
						aria-expanded={String(showMoreSettings)}
					>
						{showMoreSettings ? dictionary.hideMoreQuestionSettings : dictionary.showMoreQuestionSettings}
					</Button>
				</div>
			</div>
			{showMoreSettings && (
				<>
					<hr className={styles.dividingLine} />
					<div
						id={`extra-settings-${localId}`}
						className={styles.toolbar}
						role="region"
					>
						<ExtraSettingsTabs
							CustomSettingsComponent={CustomSettingsComponent}
							parentQuestionConfig={config}
							completeFormModel={completeFormModel}
							onChangeConfig={onChangeConfig}
						/>
					</div>
				</>
			)}
		</>
	);
};

QuestionFooter.defaultProps = {
	CustomSettingsComponent: null,
	QuestionTypeSelector: DefaultQuestionTypeSelector,
	hideRequired: false,
	hideComponentSelector: false,
	hideLabel: false,
};

QuestionFooter.propTypes = {
	completeFormModel: PropTypes.any.isRequired,
	config: PropTypes.any.isRequired,
	QuestionTypeSelector: PropTypes.elementType,
	CustomSettingsComponent: PropTypes.elementType,
	onChangeConfig: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	hideRequired: PropTypes.bool,
	hideComponentSelector: PropTypes.bool,
	hideLabel: PropTypes.bool,
};

export default QuestionFooter;
